import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Save,
  Visibility,
  CheckCircle,
  History,
  Edit
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { assistantService, promptService, authService } from '../utils/api';
import DashboardLayout from './DashboardLayout';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Prompts() {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [promptContent, setPromptContent] = useState('');
  const [initialGreeting, setInitialGreeting] = useState('');
  const [savingGreeting, setSavingGreeting] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    if (assistantId) {
      assistantService.getAssistantById(assistantId)
        .then(data => {
          setSelectedAssistant(data);
          setInitialGreeting(data.initial_greeting || '');
        })
        .catch(err => {
          toast.error(err.message || 'Failed to load assistant details.');
          navigate('/assistants');
        });
      setUserRole(authService.getUserRole());
      fetchCurrentPrompt(assistantId);
      fetchVersions(assistantId);
    }
  }, [assistantId, navigate]);

  const fetchCurrentPrompt = async (assistantId) => {
    try {
      const data = await promptService.getCurrentPrompt(assistantId);
      setCurrentPrompt(data);
      setPromptContent(data.content);
    } catch (error) {
      // If no prompt exists yet, that's okay
      if (error.detail !== 'No prompt found for this assistant') {
        console.error('Error fetching prompt:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async (assistantId) => {
    try {
      const data = await promptService.getPromptVersions(assistantId);
      setVersions(data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const handleSavePrompt = async () => {
    if (!promptContent.trim()) {
      toast.error('Prompt content cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const data = await promptService.createOrUpdatePrompt(selectedAssistant.id, promptContent);
      setCurrentPrompt(data);
      toast.success(`Prompt saved as version ${data.version_number}`);
      // Refresh versions list
      await fetchVersions(selectedAssistant.id);
    } catch (error) {
      toast.error(error.message || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGreeting = async () => {
    setSavingGreeting(true);
    try {
      const updated = await assistantService.updateAssistant(assistantId, {
        initial_greeting: initialGreeting?.trim() || undefined
      });
      toast.success('Initial greeting saved');
      // keep session selected assistant in sync (nice for header)
      const selected = assistantService.getSelectedAssistant();
      if (selected?.id === assistantId) {
        assistantService.setSelectedAssistant({ ...selected, ...updated });
      }
    } catch (error) {
      toast.error(error.message || error.detail || 'Failed to save initial greeting');
    } finally {
      setSavingGreeting(false);
    }
  };

  const handleViewVersion = async (version) => {
    setSelectedVersion(version);
    setViewDialogOpen(true);
  };

  const handleActivateVersion = async (versionNumber) => {
    try {
      await promptService.setActiveVersion(selectedAssistant.id, versionNumber);
      toast.success(`Version ${versionNumber} activated successfully`);
      // Refresh both current prompt and versions
      await fetchCurrentPrompt(selectedAssistant.id);
      await fetchVersions(selectedAssistant.id);
    } catch (error) {
      toast.error(error.message || 'Failed to activate version');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="xl">
          {/* Compact Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '1.25rem', mb: 0.5 }}>
              Prompts
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
              {selectedAssistant?.assistant_name || selectedAssistant?.name}
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: '1px solid #e2e8f0',
                minHeight: 44,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minHeight: 44,
                  color: '#64748b',
                  '&.Mui-selected': {
                    color: '#0f172a'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#3b82f6',
                  height: 2
                }
              }}
            >
              <Tab label="Prompt" />
              <Tab label="Versions" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Box>
                {/* Initial greeting — stored on the assistant, not versioned with the prompt */}
                <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem', mb: 1 }}>
                    Initial greeting
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={initialGreeting}
                    onChange={(e) => setInitialGreeting(e.target.value)}
                    placeholder="e.g. Hi {{name}}, this is Neha from F2Fintech. How are you today?"
                    disabled={!isAdmin}
                    helperText="Spoken first when the call connects. Supports {{variable}} templates from the assistant variables."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8125rem',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 1 }
                      }
                    }}
                  />
                  {isAdmin && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={savingGreeting ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                        onClick={handleSaveGreeting}
                        disabled={savingGreeting}
                        sx={{
                          minWidth: 120,
                          height: 36,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          borderColor: '#e2e8f0',
                          color: '#64748b',
                          '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff', color: '#3b82f6' }
                        }}
                      >
                        {savingGreeting ? 'Saving...' : 'Save greeting'}
                      </Button>
                    </Box>
                  )}
                </Box>

                {currentPrompt && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Chip
                      label={`Version ${currentPrompt.version_number}`}
                      size="small"
                      sx={{ 
                        bgcolor: '#3b82f6', 
                        color: 'white', 
                        fontSize: '0.75rem',
                        height: 22,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      Last updated {new Date(currentPrompt.updated_at).toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={18}
                  value={promptContent}
                  onChange={(e) => setPromptContent(e.target.value)}
                  placeholder="Enter your prompt here..."
                  disabled={!isAdmin}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace',
                      fontSize: '0.8125rem',
                      lineHeight: 1.6,
                      bgcolor: '#f8fafc',
                      '& fieldset': {
                        borderColor: '#e2e8f0'
                      },
                      '&:hover fieldset': {
                        borderColor: '#cbd5e1'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                        borderWidth: 1
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#1e293b'
                    }
                  }}
                />

                {isAdmin && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                      onClick={handleSavePrompt}
                      disabled={saving || !promptContent.trim()}
                      sx={{ 
                        minWidth: 120,
                        height: 36,
                        bgcolor: '#3b82f6',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#2563eb',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                )}

                {!isAdmin && (
                  <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    View-only access. Contact an admin to edit.
                  </Typography>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {versions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                    No versions yet. Create your first prompt.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                          Version
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                          Created
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                          Preview
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {versions.map((version) => (
                        <TableRow 
                          key={version.id} 
                          hover 
                          sx={{ 
                            '&:hover': { bgcolor: '#f8fafc' },
                            '& td': { borderBottom: '1px solid #f1f5f9' }
                          }}
                        >
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip
                              label={`v${version.version_number}`}
                              size="small"
                              sx={{
                                bgcolor: version.is_active ? '#3b82f6' : '#e2e8f0',
                                color: version.is_active ? 'white' : '#64748b',
                                fontSize: '0.75rem',
                                height: 22,
                                fontWeight: 500,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            {version.is_active && (
                              <Chip
                                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                label="Active"
                                size="small"
                                sx={{
                                  bgcolor: '#dcfce7',
                                  color: '#16a34a',
                                  fontSize: '0.75rem',
                                  height: 22,
                                  fontWeight: 500,
                                  '& .MuiChip-label': { px: 1 },
                                  '& .MuiChip-icon': { ml: 0.5, color: '#16a34a' }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.8125rem' }}>
                              {new Date(version.created_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography 
                              variant="body2" 
                              noWrap 
                              sx={{ 
                                maxWidth: 400, 
                                color: '#475569', 
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              {version.content.substring(0, 80)}...
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewVersion(version)}
                                  sx={{ 
                                    color: '#64748b',
                                    '&:hover': { 
                                      bgcolor: '#f1f5f9',
                                      color: '#0f172a'
                                    }
                                  }}
                                >
                                  <Visibility sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              {!version.is_active && isAdmin && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleActivateVersion(version.version_number)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    height: 28,
                                    px: 1.5,
                                    borderColor: '#e2e8f0',
                                    color: '#64748b',
                                    '&:hover': {
                                      borderColor: '#3b82f6',
                                      bgcolor: '#eff6ff',
                                      color: '#3b82f6'
                                    }
                                  }}
                                >
                                  Activate
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
          </Paper>

        </Container>
      </Box>

      {/* View Version Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#0f172a' }}>
              Version {selectedVersion?.version_number}
            </Typography>
            {selectedVersion?.is_active && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                label="Active"
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#16a34a',
                  fontSize: '0.75rem',
                  height: 22,
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { ml: 0.5, color: '#16a34a' }
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, borderColor: '#e2e8f0' }}>
          <Typography variant="caption" sx={{ color: '#64748b', mb: 2, display: 'block', fontSize: '0.75rem' }}>
            Created {selectedVersion && new Date(selectedVersion.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={18}
            value={selectedVersion?.content || ''}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace',
                fontSize: '0.8125rem',
                lineHeight: 1.6,
                bgcolor: '#f8fafc',
                color: '#1e293b'
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {selectedVersion && !selectedVersion.is_active && isAdmin && (
            <Button
              variant="contained"
              onClick={() => {
                handleActivateVersion(selectedVersion.version_number);
                setViewDialogOpen(false);
              }}
              sx={{
                bgcolor: '#3b82f6',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                height: 36,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#2563eb',
                  boxShadow: 'none'
                }
              }}
            >
              Activate
            </Button>
          )}
          <Button 
            onClick={() => setViewDialogOpen(false)}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Prompts;
